import { useAuth } from '@verantos-dev/core-auth';

import { useProjectArtifacts } from '../../../hooks/useProjectArtifacts';

/**
 * Resolve the draft's owner + last-modified (neither is on the preview detail)
 * and whether the CURRENT user owns it, from the project-artifact manifest.
 * Match by the stable catalog id (minted at save) or the preview id; fall back
 * to the catalog-unique name in case the row is keyed differently.
 *
 * Ownership drives the draft's edit affordances (Save / Change file) — only the
 * owner sees them. When the manifest has loaded but the row ISN'T resolvable —
 * the common case for a brand-new draft the current user just created, whose
 * manifest refetch lags the navigation off a warm cache — default to the current
 * user being the owner and surface their name. A draft made by someone else is
 * always an existing project artifact, so it resolves to their row and correctly
 * reads non-owner; the server is the hard backstop either way.
 *
 * While the manifest is still LOADING (a cold refresh straight onto the draft,
 * no cached list), ownership is genuinely unknown — ``isResolving`` is true. We
 * must NOT optimistically claim the current user here, or opening someone else's
 * draft flashes an owner view (their name, Save + Change file) before flipping to
 * read-only. Callers suppress the meta row + edit affordances until it resolves.
 * (A warm-cache refetch is ``isFetching``, not ``isLoading``, so the brand-new-
 * draft optimism above is preserved.)
 */
export const useDraftConceptGroupOwnership = (
  projectId: string,
  match: {
    createdGroupId: string | null;
    previewId: string | null | undefined;
    conceptGroupName: string | null | undefined;
  }
) => {
  const {
    user: { userId, auth0Id, firstName, lastName },
  } = useAuth();
  const { artifacts, isLoading: manifestLoading } = useProjectArtifacts(projectId);
  const rows = artifacts.filter((artifact) => artifact.kind === 'concept_group');
  const draftRow =
    rows.find(
      (artifact) => artifact.id === match.createdGroupId || artifact.id === match.previewId
    ) ??
    rows.find(
      (artifact) => Boolean(match.conceptGroupName) && artifact.name === match.conceptGroupName
    );
  // Ownership is only unknown while the manifest is still loading AND we haven't
  // found the row yet; once loaded, an absent row is a brand-new draft → current user.
  const isResolving = manifestLoading && !draftRow;
  const isOwner = draftRow ? [userId, auth0Id].includes(draftRow.owner.id) : !isResolving;
  const currentUserName = `${firstName ?? ''} ${lastName ?? ''}`.trim();
  const ownerName = draftRow?.owner.name ?? (isOwner ? currentUserName || undefined : undefined);
  const lastModifiedIso = draftRow?.updatedAt ?? null;
  return { isOwner, ownerName, lastModifiedIso, isResolving };
};
